<script setup lang="ts">
import { useTagStore, handleAddTag } from '@/stores/tags';
import IconRanaTagSearch from '/assets/icons/IconRanaTagSearch.vue';
import {
  products,
  filteredProducts,
  searchQuery,
  selectedCategory,
} from '@/components/Products';

import RanaTag from '@/components/RanaTag.vue';
import SearchableTextInput from '@/components/SearchableTextInput.vue';
import { computed, ref, toRef, watch, onMounted } from 'vue';
import IconTrash from '@/components/icons/IconTrash.vue';
import { storeToRefs } from 'pinia';
import { useNuxtApp } from '#app';

const { $vm: vm } = useNuxtApp();
const tagStore = useTagStore();
const { allTags } = storeToRefs(tagStore);

// define emits up front
const emit = defineEmits<{
  (e: 'isOpen', isOpen: boolean): void;
}>();

// build master tag list on mount
function loadTags() {
  const map: Record<string, boolean> = { ...allTags.value };
  products.value.forEach((p) => p.tags?.forEach((t) => (map[t] = true)));
  allTags.value = map;
}
onMounted(loadTags);

const searchInputClicked = ref(false);
const isOpen = computed(
  () => tagStore.selected.length > 0 || searchInputClicked.value
);
watch(isOpen, (o) => emit('isOpen', o), { immediate: true });

const addHover = ref(false);

function onAddDragOver(ev: DragEvent) {
  ev.preventDefault();
  addHover.value = true;
}
function onAddDragLeave(ev: DragEvent) {
  ev.preventDefault();
  addHover.value = false;
}
function onAddDrop(ev: DragEvent) {
  ev.preventDefault();
  addHover.value = false;

  const tag = ev.dataTransfer?.getData('text');
  if (!tag) return;

  if (!allTags.value[tag]) {
    allTags.value = { ...allTags.value, [tag]: true };
  }
  if (!tagStore.selected.includes(tag)) {
    tagStore.select(tag);
  }
}

const wideTrash = ref(false);
const hoveringTrash = ref(false);

function dragOver(ev: DragEvent) {
  ev.preventDefault();
  hoveringTrash.value = true;
}
function dragLeave(ev: DragEvent) {
  ev.preventDefault();
  hoveringTrash.value = false;
}
function drop(ev: DragEvent) {
  ev.preventDefault();
  const tag = ev.dataTransfer?.getData('text');
  if (tag) tagStore.unselect(tag);
  wideTrash.value = false;
  hoveringTrash.value = false;
}
</script>

<template>
  <div class="relative flex flex-col justify-between rounded-lg bg-neutral">
    <div ref="dragContainer" class="relative flex flex-col">
      <!-- 1) Selected tags === now the add-tag drop zone -->
      <div
        class="z-10 flex-wrap content-start gap-2 overflow-y-auto p-2 transition-colors border-2"
        :class="[
          isOpen ? 'h-5/6' : 'hidden',
          addHover
            ? 'border-warning bg-warning-content rounded-lg'
            : 'border-transparent'
        ]"
        @dragenter.prevent="onAddDragOver"
        @dragover.prevent="onAddDragOver"
        @dragleave.prevent="onAddDragLeave"
        @drop.prevent="onAddDrop"
      >
        <RanaTag
          v-for="tag in tagStore.selected"
          :key="tag"
          class="cursor-grab"
          :tag="tag"
          @update:is-dragging="(d) => (wideTrash = d)"
          @onRemove="() => tagStore.unselect(tag)"
        />
      </div>

      
      <div class="z-20 flex flex-row items-center space-x-2 px-2">
        <SearchableTextInput
          :items="toRef(tagStore.availableTags)"
          :selected="toRef(tagStore.selected)"
          :onEnter="(t) => handleAddTag(t, tagStore)"
          :onUpdate:optionSelect="(t) => tagStore.select(t)"
          :onUpdate:searchQueryChanged="(q) => tagStore.fetchTags(q)"
          :onclick="() => (searchInputClicked = true)"
          :icon="IconRanaTagSearch"
          @inputLostFocus="() => (searchInputClicked = false)"
          placeholder="Search Tags"
        />

        <button
          :disabled="tagStore.selected.length === 0"
          class="flex h-12 items-center justify-center rounded-lg border-2 border-primary transition-[width] delay-150 duration-200 ease-in-out [&>*]:text-primary"
          :class="[
            wideTrash
              ? 'w-full border-2 border-warning [&>*]:text-warning'
              : 'w-12',
            hoveringTrash
              ? 'border-warning bg-warning-content'
              : '',
            tagStore.selected.length === 0
              ? 'disabled cursor-not-allowed opacity-10'
              : 'hover:border-warning hover:bg-warning-content hover:text-warning [&>*]:hover:text-warning'
          ]"
          @dragover.stop.prevent="dragOver"
          @dragleave.stop.prevent="dragLeave"
          @drop.stop.prevent="drop"
          @click="tagStore.unselectAll"
        >
          <IconTrash
            class="fill-current"
            :class="[
              hoveringTrash && tagStore.selected.length !== 0
                ? 'bg-warning-content fill-warning text-warning'
                : ''
            ]"
          />
        </button>
      </div>
    </div>
  </div>
</template>
